import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { MessageButton, ConversationItem, LoadingSpinner } from '../common'
import { useMessageNotification, useConversations } from '../hooks'
import MessageModal from '../modals/MessageModal'

interface GlobalMessageNotificationProps {
  className?: string
}

export default function GlobalMessageNotification({
  className = '',
}: GlobalMessageNotificationProps) {
  const [showModal, setShowModal] = useState(false)
  const [selectedRecipient, setSelectedRecipient] = useState<{
    id: string
    name: string
    email: string
    projectId: string
    projectName: string
  } | null>(null)

  const navigate = useNavigate()
  const { user } = useAuth()

  const { totalUnreadCount, refreshTotalUnreadCount } = useMessageNotification({
    enablePolling: true,
    pollingInterval: 30000,
  })

  const { allProjectConversations, loading, error, loadAllProjectConversations, clearError } =
    useConversations()

  const handleOpenMessages = async () => {
    await loadAllProjectConversations()
    setShowModal(true)
  }

  const handleOpenConversation = (
    participantId: string,
    participantName: string,
    participantEmail: string,
    projectId: string,
    projectName: string
  ) => {
    setSelectedRecipient({
      id: participantId,
      name: participantName,
      email: participantEmail,
      projectId,
      projectName,
    })
    setShowModal(false)
  }

  const handleGoToProject = (projectId: string) => {
    // Decide navigation path based on user role
    if (user?.is_admin) {
      navigate(`/projects/${projectId}/review`)
    } else {
      navigate(`/projects/${projectId}`)
    }
    setShowModal(false)
  }

  const handleCloseMessage = () => {
    setSelectedRecipient(null)
    refreshTotalUnreadCount()
  }

  const handleRetry = () => {
    clearError()
    loadAllProjectConversations()
  }

  return (
    <>
      <MessageButton
        onClick={handleOpenMessages}
        unreadCount={totalUnreadCount}
        className={className}
        title="Messages"
      />

      {/* Global message list modal */}
      {showModal && !selectedRecipient && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black bg-opacity-25"
              onClick={() => setShowModal(false)}
            />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[600px] overflow-hidden">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Messages {totalUnreadCount > 0 ? `(${totalUnreadCount} unread)` : ''}
                  </h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[520px]">
                {error && (
                  <div className="p-4 bg-red-50 border-b border-red-200 text-red-600 text-sm">
                    {error}
                    <button onClick={handleRetry} className="ml-2 underline hover:no-underline">
                      Retry
                    </button>
                  </div>
                )}
                {loading ? (
                  <div className="p-8">
                    <LoadingSpinner message="Loading conversations..." />
                  </div>
                ) : allProjectConversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <MessageButton
                      onClick={() => {}}
                      className="w-12 h-12 mx-auto mb-2 text-gray-300 cursor-default"
                      size="lg"
                    />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  <div>
                    {allProjectConversations.map(projectConv => (
                      <div
                        key={projectConv.project_id}
                        className="border-b border-gray-200 last:border-b-0"
                      >
                        {/* Project Header */}
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-gray-900">
                              {projectConv.project_name}
                            </h4>
                            <button
                              onClick={() => handleGoToProject(projectConv.project_id)}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Go to Project
                            </button>
                          </div>
                          {projectConv.unread_count > 0 && (
                            <span className="text-xs text-red-600">
                              {projectConv.unread_count} unread message
                              {projectConv.unread_count > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        {/* Conversations in this project */}
                        {projectConv.conversations.map(conversation => (
                          <ConversationItem
                            key={conversation.participant_id}
                            participantName={conversation.participant_name}
                            participantEmail={conversation.participant_email}
                            lastMessage={conversation.last_message}
                            lastMessageAt={conversation.last_message_at}
                            unreadCount={conversation.unread_count}
                            onClick={() =>
                              handleOpenConversation(
                                conversation.participant_id,
                                conversation.participant_name,
                                conversation.participant_email,
                                projectConv.project_id,
                                projectConv.project_name
                              )
                            }
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual Message Modal */}
      {selectedRecipient && (
        <MessageModal
          isOpen={true}
          onClose={handleCloseMessage}
          projectId={selectedRecipient.projectId}
          recipientId={selectedRecipient.id}
          recipientName={selectedRecipient.name}
          recipientEmail={selectedRecipient.email}
        />
      )}
    </>
  )
}
